import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import FormField from "../components/forms/FormField";
import LoadingSpinner from "../components/common/LoadingSpinner";
import Logo from "../components/common/Logo";
import { ROUTES } from "../utils/constants";
import firestoreService from "../services/firestoreService";
import {
  validatePassword,
  validatePasswordStrength,
} from "../utils/validation";

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [status, setStatus] = useState("validating"); // validating | valid | expired | invalid | success
  const [email, setEmail] = useState("");
  const [tokenDocId, setTokenDocId] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    feedback: [],
  });

  useEffect(() => {
    if (!token) {
      setStatus("invalid");
      return;
    }
    firestoreService
      .validatePasswordResetToken(token)
      .then(({ email: e, docId }) => {
        setEmail(e);
        setTokenDocId(docId);
        setStatus("valid");
      })
      .catch((err) => {
        setStatus(err.message.includes("expired") ? "expired" : "invalid");
      });
  }, [token]);

  useEffect(() => {
    setPasswordStrength(
      newPassword
        ? validatePasswordStrength(newPassword)
        : { score: 0, feedback: [] },
    );
  }, [newPassword]);

  const validate = () => {
    const e = {};
    if (!newPassword) e.newPassword = "Password is required.";
    else if (!validatePassword(newPassword))
      e.newPassword =
        "Password must be at least 8 characters with uppercase, lowercase, numbers, and symbols.";
    if (!confirmPassword) e.confirmPassword = "Please confirm your password.";
    else if (newPassword !== confirmPassword)
      e.confirmPassword = "Passwords do not match.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      // Verify account exists
      const account = await firestoreService.getUserAccount(email);
      if (!account) throw new Error("Account not found.");

      // Update Firebase Auth password using the REST API.
      // We look up the user's UID by email (no sign-in required),
      // then use accounts:update with the idToken obtained via sign-in.
      // Since we may not know the current Firebase Auth password (it may differ
      // from temporaryPassword if the user already changed it), we use the
      // stored credential — but fall back gracefully if that fails.
      const apiKey = import.meta.env.VITE_FIREBASE_API_KEY;

      // Try credentials in order: currentPassword (most recent) then temporaryPassword (original)
      const credentialsToTry = [
        account.currentPassword, // most recent password if already reset before
        account.temporaryPassword, // original one-time credential
      ];

      let idToken = null;
      let lastSignInError = null;

      for (const cred of credentialsToTry) {
        if (!cred) continue;
        try {
          const res = await fetch(
            `https://identitytoolkit.googleapis.com/v1/accounts:signInWithPassword?key=${apiKey}`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                email,
                password: cred,
                returnSecureToken: true,
              }),
            },
          );
          if (res.ok) {
            const data = await res.json();
            idToken = data.idToken;
            break;
          }
          const err = await res.json().catch(() => ({}));
          lastSignInError = err?.error?.message || "Sign-in failed";
        } catch (e) {
          lastSignInError = e.message;
        }
      }

      if (!idToken) {
        // All known credentials failed — Firebase Auth is out of sync with Firestore.
        // This affects legacy users who changed password before the currentPassword
        // tracking was added. Throw a clear, actionable error.
        throw new Error(
          "Your stored credentials are out of sync. Please log in with your current password, " +
            "then use the Change Password option to set a new password. " +
            "If you cannot log in, contact an administrator to reset your account.",
        );
      } else {
        // Update Firebase Auth password
        const updateRes = await fetch(
          `https://identitytoolkit.googleapis.com/v1/accounts:update?key=${apiKey}`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              idToken,
              password: newPassword,
              returnSecureToken: false,
            }),
          },
        );
        if (!updateRes.ok) {
          const err = await updateRes.json().catch(() => ({}));
          console.error("Firebase Auth password update failed:", err);
        }
      }

      // Always update Firestore: store new password as the current credential
      // AND preserve the original temporaryPassword separately so future sign-ins work.
      await firestoreService.storeNewPassword(email, newPassword);

      // Mark token as used
      await firestoreService.consumePasswordResetToken(tokenDocId);

      setStatus("success");
    } catch (err) {
      console.error("Reset password error:", err);
      const msg =
        err.code === "auth/invalid-credential" ||
        err.code === "auth/wrong-password"
          ? "Authentication failed. Please request a new reset link."
          : err.message || "Failed to reset password. Please try again.";
      setErrors({ submit: msg });
    } finally {
      setLoading(false);
    }
  };

  const strengthClass = () => {
    if (passwordStrength.score === 0) return "";
    if (passwordStrength.score <= 2) return "weak";
    if (passwordStrength.score <= 4) return "medium";
    return "strong";
  };

  return (
    <div className="admin-login-page login-page">
      <div className="page-header">
        <div className="container">
          <Logo size="large" className="page-logo" />
          <h1 className="page-title">Mirt Sew</h1>
          <h2 className="page-title-2">Set New Password</h2>
        </div>
      </div>

      <div className="page-content">
        <div className="container">
          <div className="login-form-container">
            <div className="login-card">
              <div className="card-body">
                {status === "validating" && (
                  <div style={{ textAlign: "center", padding: "2rem" }}>
                    <LoadingSpinner
                      size="medium"
                      message="Validating reset link..."
                    />
                  </div>
                )}

                {status === "expired" && (
                  <div style={{ textAlign: "center", padding: "1rem 0" }}>
                    <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>
                      ⏰
                    </div>
                    <p
                      style={{
                        color: "var(--color-danger)",
                        fontWeight: "500",
                        marginBottom: "0.5rem",
                      }}
                    >
                      Link Expired
                    </p>
                    <p
                      style={{
                        color: "var(--text-secondary)",
                        fontSize: "0.9rem",
                        marginBottom: "1.5rem",
                      }}
                    >
                      This reset link has expired (valid for 10 minutes only).
                      Please request a new one.
                    </p>
                    <button
                      type="button"
                      className="submit-button enabled"
                      onClick={() => navigate(ROUTES.FORGOT_PASSWORD)}
                    >
                      Request New Link
                    </button>
                  </div>
                )}

                {status === "invalid" && (
                  <div style={{ textAlign: "center", padding: "1rem 0" }}>
                    <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>
                      ❌
                    </div>
                    <p
                      style={{
                        color: "var(--color-danger)",
                        fontWeight: "500",
                        marginBottom: "0.5rem",
                      }}
                    >
                      Invalid Link
                    </p>
                    <p
                      style={{
                        color: "var(--text-secondary)",
                        fontSize: "0.9rem",
                        marginBottom: "1.5rem",
                      }}
                    >
                      This reset link is invalid or has already been used.
                    </p>
                    <button
                      type="button"
                      className="submit-button enabled"
                      onClick={() => navigate(ROUTES.FORGOT_PASSWORD)}
                    >
                      Request New Link
                    </button>
                  </div>
                )}

                {status === "success" && (
                  <div style={{ textAlign: "center", padding: "1rem 0" }}>
                    <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>
                      ✅
                    </div>
                    <p
                      style={{
                        color: "var(--color-success)",
                        fontWeight: "500",
                        marginBottom: "0.5rem",
                      }}
                    >
                      Password Reset!
                    </p>
                    <p
                      style={{
                        color: "var(--text-secondary)",
                        fontSize: "0.9rem",
                        marginBottom: "1.5rem",
                      }}
                    >
                      Your password has been updated. You can now log in with
                      your new password.
                    </p>
                    <button
                      type="button"
                      className="submit-button enabled"
                      onClick={() => navigate(ROUTES.ADMIN_LOGIN)}
                    >
                      Go to Login
                    </button>
                  </div>
                )}

                {status === "valid" && (
                  <form
                    onSubmit={handleSubmit}
                    className="login-form"
                    noValidate
                  >
                    <p
                      style={{
                        color: "var(--text-secondary)",
                        fontSize: "0.875rem",
                        marginBottom: "1.25rem",
                      }}
                    >
                      Setting new password for{" "}
                      <strong style={{ color: "var(--text-primary)" }}>
                        {email}
                      </strong>
                    </p>

                    {errors.submit && (
                      <div
                        style={{
                          color: "var(--color-danger)",
                          background: "var(--color-danger-bg)",
                          padding: "0.75rem",
                          borderRadius: "0.375rem",
                          marginBottom: "1rem",
                          fontSize: "0.875rem",
                        }}
                      >
                        {errors.submit}
                      </div>
                    )}

                    <div className="password-field-container">
                      <FormField
                        label="New Password"
                        name="newPassword"
                        type="password"
                        value={newPassword}
                        onChange={(e) => {
                          setNewPassword(e.target.value);
                          setErrors({});
                        }}
                        error={errors.newPassword}
                        required
                        autoComplete="new-password"
                        placeholder="Enter new password"
                        disabled={loading}
                      />
                      {newPassword && (
                        <div className="password-strength">
                          <div className="strength-bar">
                            <div
                              className={`strength-fill ${strengthClass()}`}
                              style={{
                                width: `${(passwordStrength.score / 5) * 100}%`,
                              }}
                            />
                          </div>
                          <div className="strength-info">
                            <span
                              className={`strength-text ${strengthClass()}`}
                            >
                              {
                                [
                                  "",
                                  "Weak",
                                  "Weak",
                                  "Medium",
                                  "Medium",
                                  "Strong",
                                ][passwordStrength.score]
                              }
                            </span>
                            {passwordStrength.feedback.length > 0 && (
                              <ul className="strength-feedback">
                                {passwordStrength.feedback.map((f, i) => (
                                  <li key={i} className="feedback-item">
                                    {f}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    <FormField
                      label="Confirm New Password"
                      name="confirmPassword"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        setErrors({});
                      }}
                      error={errors.confirmPassword}
                      required
                      autoComplete="new-password"
                      placeholder="Confirm new password"
                      disabled={loading}
                    />

                    <div className="form-actions">
                      <button
                        type="submit"
                        className={`submit-button ${newPassword && confirmPassword && !loading ? "enabled" : "disabled"}`}
                        disabled={!newPassword || !confirmPassword || loading}
                      >
                        {loading ? (
                          <>
                            <LoadingSpinner size="small" />
                            <span>Updating...</span>
                          </>
                        ) : (
                          "Set New Password"
                        )}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
